import {SC_User} from "../SC_User";
import {SC_CrewType} from "../enums/SC_CrewType";
import {SC_WorkorderObject} from "../SC_WorkorderObject";
import {SC_FailedFence} from "../SC_FailedFence";
import {SC_PaycomGroup} from "../SC_PaycomGroup";
import {SC_ForceType} from "../enums/SC_ForceType";
import {SC_ServiceInstancePhase} from "../enums/SC_ServiceInstancePhase";
import {ApiDateWrapper} from "../ApiDateWrapper";
import {SC_ServiceFile} from "../files/SC_ServiceFile";
import {SC_ImageStatuses} from "../enums/SC_ImageStatuses";

export interface SC_CrewBaseConstructorProps {
    internalID?: number,
    alias?: string,
    states?: string[],
    active?: boolean,
    leadID?: number,
    members?: number[],
    crewType?: SC_CrewType,
}

export interface CrewScoreResult {
    qualityScore: number;
    ivrCompletion: {good: number; meh: number; bad: number};
    ivrDistance: {good: number; meh: number; bad: number};
    insiteDisparity: {good: number; bad: number};
    imageQuality: {good: number; meh: number; bad: number};
    qualityScores: {good: number; meh: number; bad: number};
    overallScore: number;
    maxScore: number;
    mylog: string[];
    allPictures: SC_ServiceFile[]
    allGroups: SC_PaycomGroup[]
    crewState: string | null
}

export class SC_CrewBase {
    static inputKeys: Record<string, any> = {
        internalID: 'id',
        alias: 'alias',
        states: 'states',
        active: 'active',
        leadID: 'lead',
        members: 'members',
        crewType: 'crew_type',
    } as const;

    public internalID: number | null;
    public alias: string | null;
    public states: string[] | null;
    public active: boolean | null;
    public leadID: number | null;
    public lead: SC_User | null;
    public memberIDs: number[] | null;
    public members: SC_User[] | null;
    public crewType: SC_CrewType;

    constructor({
                    internalID = null,
                    alias = null,
                    states = null,
                    active = null,
                    leadID = null,
                    members = null,
                    crewType = new SC_CrewType(SC_CrewType.NULL),
                }: SC_CrewBaseConstructorProps = {}) {
        this.internalID = internalID;
        this.alias = alias;
        this.states = states?.filter(Boolean);
        this.active = active;
        this.leadID = leadID;
        this.lead = null;
        this.memberIDs = members;
        this.members = null;
        this.crewType = crewType;
    }

    static basicAPIStuff(jsonin: Record<string, any>): SC_CrewBaseConstructorProps {
        return {
            internalID: jsonin[SC_CrewBase.inputKeys.internalID] ?? null,
            alias: jsonin[SC_CrewBase.inputKeys.alias] ?? null,
            states: jsonin[SC_CrewBase.inputKeys.states] ?? null,
            active: jsonin[SC_CrewBase.inputKeys.active] ?? null,
            leadID: jsonin[SC_CrewBase.inputKeys.leadID] ?? null,
            members: jsonin[SC_CrewBase.inputKeys.members] ?? null,
            crewType: SC_CrewType.fromAPI(jsonin[SC_CrewBase.inputKeys.crewType]),
        };
    }

    scavengeUsers(users: SC_User[]): void {
        if (this.leadID) {
            let lead = users.find(user => user.insiteID === this.leadID);
            if (lead)
                this.lead = lead;
        }

        if (this.memberIDs) {
            this.members = this.memberIDs.map(memid => users.find(user => user.insiteID === memid));
        }
    }

    get displayText(): string | null {
        return this.alias;
    }

    toAPI(): Record<string, any> {
        return {
            [SC_CrewBase.inputKeys.internalID]: this.internalID,
            [SC_CrewBase.inputKeys.alias]: this.alias,
            [SC_CrewBase.inputKeys.states]: this.states,
            [SC_CrewBase.inputKeys.active]: this.active,
            [SC_CrewBase.inputKeys.leadID]: this.leadID,
            [SC_CrewBase.inputKeys.members]: this.members,
            [SC_CrewBase.inputKeys.crewType]: this.crewType.toAPI(),
        }
    }

    _merge(other: SC_CrewBase) {
        if (other.internalID !== null) this.internalID = other.internalID;
        if (other.alias !== null) this.alias = other.alias;
        if (other.states !== null) this.states = other.states;
        if (other.active !== null) this.active = other.active;
        if (other.leadID !== null) this.leadID = other.leadID;
        if (other.memberIDs !== null) this.memberIDs = other.memberIDs;
        if (!other.crewType.is(SC_CrewType.NULL)) this.crewType = other.crewType;
    }

    _clone(other: SC_CrewBase) {
        if (other.internalID !== null) this.internalID = other.internalID;
        if (other.alias !== null) this.alias = other.alias;
        if (other.states !== null) this.states = other.states;
        if (other.active !== null) this.active = other.active;
        if (other.leadID !== null) this.leadID = other.leadID;
        if (other.memberIDs !== null) this.memberIDs = other.memberIDs;
        if (other.members !== null) this.members = other.members;
        if (other.crewType && !other.crewType.is(SC_CrewType.NULL)) this.crewType = other.crewType;
    }

    static sortCrews(a: SC_CrewBase, b: SC_CrewBase): number {
        return (a.alias ?? "").localeCompare(b.alias ?? "");
    }

    /**
     * Calculates crew performance scores based on services, workorders, failures, and punch groups.
     * @param workorders - Array of workorders for this crew
     * @param failures - Array of failed fence records
     * @param groups - Array of punch groups
     * @returns Score result object with all calculated metrics
     */
    calculateScores(
        workorders: SC_WorkorderObject[],
        failures: SC_FailedFence[],
        groups: SC_PaycomGroup[],
    ): CrewScoreResult | null {
        if (!workorders.length) return null;

        // Filter to crew's services and workorders
        const myWorkorders = workorders.filter(wo => wo.services.some(service => service.crewID === this.internalID) && wo.locationID !== 1904);
        const myServices = myWorkorders.map(wo => wo.services.filter(s => s.crewID === this.internalID && s.phase.is(SC_ServiceInstancePhase.COMPLETE))).flat();
        const myFailures = failures.filter(f => myServices.some(serv => serv.internalID === f.serviceID));

        if (!myServices.length || !myWorkorders.length) return null;

        const mySIDs = new Set(myServices.map(it => it.internalID));

        // Filter punch groups to those with relevant services
        const pertinentGroups = groups.filter(it => it.children.some(child => mySIDs.has(child.resolvedServiceID)));

        // Filter workorders with quality data
        const qualityWos = myWorkorders.filter(wo => wo.quality !== null);

        // Filter services by force type
        const unforced = myServices.filter(it => it.forceType.is(SC_ForceType.NULL));
        const halfunforced = myServices.filter(it => it.forceType.is(SC_ForceType.OUT_FAILURE) || it.forceType.is(SC_ForceType.OUT_INSITE_FAILURE));

        const myGIDSet = new Set(pertinentGroups.map(it => it.gid));
        const myUIDSet = new Set(pertinentGroups.map(it => it.uid).concat(this.lead?.paycomID ?? []).concat(this.members.map(it => it.paycomID).filter(Boolean)));

        // Group punch groups by service
        const myFiles = myWorkorders.map(it => it.allImages.filter(it => it.service.crewID === this.internalID)).flat(1).sort((a, b) => b.taken_date.toMillis() - a.taken_date.toMillis());
        const filesByService: Record<number, SC_ServiceFile[]> = myFiles.reduce((out: Record<number, SC_ServiceFile[]>, curr) => {
            if (!(curr.service_id in out))
                out[curr.service_id] = [];

            out[curr.service_id].push(curr);

            return out;
        }, {});

        // Group punch groups by service
        const groupByService: Record<number, SC_PaycomGroup[]> = pertinentGroups.reduce((out: Record<number, SC_PaycomGroup[]>, curr) => {
            Array.from(new Set(curr.children.filter(it => it.resolvedServiceID && mySIDs.has(it.resolvedServiceID)).map(it => it.resolvedServiceID))).forEach((sid) => {
                if (!(sid in out)) out[sid] = [];
                out[sid].push(curr);
            });
            return out;
        }, {});

        // Calculate bonus score from lonely groups
        const groupedLoners = this.calculateGroupedLoners(groups, myGIDSet, myUIDSet);

        // @ts-ignore
        const bonusEntrants: [number, SC_PaycomGroup[]][] = Object.entries(groupedLoners).map(([lid, ps]) => {
            return ps.map(p => [Number(lid), p]);
        }).flat();

        // Calculate worst distances per service
        const worstDistances = Object.entries(groupByService).map(([servid, punches]) => {
            return punches.reduce((out: number, curr) => {
                return Math.max(out, curr.worstGeoFenceDistance(Number(servid)));
            }, -1);
        }).concat(Object.entries(groupedLoners).map(([lid, punches]) => {
            return punches.flat().reduce((out: number, curr) => {
                return Math.max(out, curr.worstGeoFenceToLoc(Number(lid)));
            }, -1);
        })).filter(it => it >= 0);

        // Calculate disparities (timing disagreements)
        const disparities = Object.entries(groupByService).map(([servid, punches]) => {
            return punches.reduce((out: number, curr) => {
                return Math.max(out, curr.insiteDisagreement(Number(servid)));
            }, -1);
        }).concat(Object.entries(groupedLoners).map(([_lid, punches]) => {
            const lid = Number(_lid);
            return punches.filter(it => it.length > 1).map(groups => {
                const earliestStart = groups.reduce((out: ApiDateWrapper, curr) => curr.locStart(lid) < out ? curr.locStart(lid) : out, groups[0].locStart(lid));
                return groups.reduce((out: number, other) => Math.max(out, (other.locStart(lid).toMillis() - earliestStart.toMillis()) / 1000), -1);
            });
        }).flat()).filter(it => it >= 0);

        // Calculate file datas
        const filestuff = myServices.map((service) => {
            if (!(service.internalID in filesByService))
                return 0;

            const files = filesByService[service.internalID];

            if (files.some(file => file.reported))
                return 0;

            switch (service.picturesStatus(files).toAPI()) {
                default:
                case SC_ImageStatuses.COMPLETE:
                    return 2;
                case SC_ImageStatuses.MISSING:
                    return 1;
                case SC_ImageStatuses.NONE:
                    return 0;
            }
        });

        // Calculate overall score
        const baze = this.isSub ? 10 : 20;
        let overallScore = 0;
        const mylog: string[] = [];

        myServices.forEach(it => {
            if (it.forceType.is(SC_ForceType.TRUANCY)) {
                overallScore += 0;
                return;
            }

            let base = baze;

            if (it.internalID in filesByService) {
                let files = filesByService[it.internalID];

                if (files.some(it => it.reported))
                    base -= 2;

                switch (it.picturesStatus(files).toAPI()) {
                    case SC_ImageStatuses.MISSING:
                        base -= 2;
                        break;
                    case SC_ImageStatuses.NONE:
                        base -= 5;
                }
            } else {
                base -= 5;
            }

            const parent = myWorkorders.find(parent => parent.internalID === it.parentID);
            if (parent?.quality) {
                if (parent.quality <= 2) {
                    base -= 5;
                    mylog.push("Terrible Q");
                } else if (parent.quality <= 3) {
                    base -= 3;
                    mylog.push("OK Q");
                }
            }

            if (it.forceType.is(SC_ForceType.OUT_FAILURE)) {
                base -= 2;
                mylog.push("Force Out");
            } else if (it.forceType.is(SC_ForceType.TOTAL_FAILURE)) {
                base -= 5;
                mylog.push("Force Tot");
            }

            if (!(it.internalID in groupByService)) {
                overallScore += base;
                return;
            }

            const groups = groupByService[it.internalID];

            if (groups.some(g => Math.abs(g.insiteDisagreement(it.internalID)) > 15 * 60)) {
                base -= 3;
                mylog.push("Insite Bad");
            }

            if (groups.some(g => g.worstGeoFenceDistance(it.internalID) > 0.25)) {
                base -= 5;
                mylog.push("Fence Bad");
            }

            overallScore += base;
        });

        let bonusScore = 0;
        bonusEntrants.forEach(([lid, groups]) => {
            let base = 10;

            const earliestStart = groups.reduce((out: ApiDateWrapper, curr) => curr.locStart(lid) < out ? curr.locStart(lid) : out, groups[0].locStart(lid));
            if (groups.some(other => other.locStart(lid).toMillis() - earliestStart.toMillis() > 15 * 60 * 1000)) {
                base -= 3;
            }

            if (groups.some(g => g.worstGeoFenceToLoc(lid) > 0.25)) {
                base -= 7;
            }

            bonusScore += base;
        });

        const maxScore = myServices.length * baze + bonusEntrants.length * 10;

        // console.log(this.alias, groupedLoners, groupByService, pertinentGroups.concat(Object.values(groupedLoners).flat(2)));

        const allGroups: SC_PaycomGroup[] = Object.values(pertinentGroups.concat(Object.values(groupedLoners).flat(2)).reduce((out, curr) => {
            if (!(curr.gid in out))
                out[curr.gid] = curr;

            return out;
        }, {}));

        return {
            qualityScore: qualityWos.reduce((sum, wo) => sum + wo.quality, 0),
            ivrCompletion: {
                good: unforced.length,
                meh: halfunforced.length,
                bad: myServices.length - unforced.length - halfunforced.length
            },
            ivrDistance: {
                good: worstDistances.filter(it => it < 0.25).length,
                meh: worstDistances.filter(it => it >= 0.25 && it <= 1).length,
                bad: worstDistances.filter(it => it > 1).length
            },
            insiteDisparity: {
                good: disparities.filter(it => it < 15 * 60).length,
                bad: disparities.filter(it => it >= 15 * 60).length
            },
            imageQuality: {
                good: filestuff.filter(it => it === 2).length,
                meh: filestuff.filter(it => it === 1).length,
                bad: filestuff.filter(it => it === 0).length
            },
            overallScore: overallScore + bonusScore,
            maxScore,
            mylog,
            qualityScores: {
                good: qualityWos.filter(it => it.quality === 5 || it.quality === 4).length,
                meh: qualityWos.filter(it => it.quality === 3).length,
                bad: qualityWos.filter(it => it.quality === 2 || it.quality === 1).length
            },
            allPictures: myFiles,
            allGroups: allGroups,
            crewState: this.calculateCrewState(pertinentGroups),
        };
    }

    /**
     * Calculates grouped loners - punch groups that don't match any service but belong to crew members
     */
    private calculateGroupedLoners(
        groups: SC_PaycomGroup[],
        gidSet: Set<string>,
        uidSet: Set<number>,
    ): Record<number, SC_PaycomGroup[][]> {
        const lonelyGroups = groups.filter(group => !gidSet.has(group.gid) && uidSet.has(group.uid));

        return lonelyGroups.reduce((out: Record<number, SC_PaycomGroup[][]>, curr) => {
            const mylocs = Array.from(new Set(curr.children.map(it => [it.resolvedLocationStartID, it.resolvedLocationEndID]).flat().filter(Boolean)));

            mylocs.forEach((lid) => {
                if (!curr.locStart(lid) || !curr.locEnd(lid)) return;

                if (!(lid in out)) out[lid] = [];

                const lidStart = curr.locStart(lid).toMillis();
                let friendly = out[lid].map(gs => gs.some(thing => Math.abs(thing.locStart(lid).toMillis() - lidStart) < 1000 * 3600 * 6));

                let ct = friendly.reduce((sum: number, val: boolean) => sum + (val ? 1 : 0), 0);

                if (!ct) {
                    out[lid].push([curr]);
                } else if (ct === 1) {
                    out[lid][friendly.findIndex(Boolean)].push(curr);
                }
            });

            return out;
        }, {});
    }

    calculateCrewState(
        pertinentGroups: SC_PaycomGroup[],
    ): string | null {
        if (pertinentGroups.length) {
            return pertinentGroups[0].state;
        } else if (this.crewType.is(SC_CrewType.SUB)) {
            return null;
        } else if (this.crewType.is(SC_CrewType.EMPLOYEE_BACKED)) {
            return this.lead?.state ?? null;
        }
        return null;
    }

    get isSub(): boolean {
        return this.crewType?.is(SC_CrewType.SUB) ?? false;
    }

    applyToFormData(formData: FormData, oldValue?: SC_CrewBase) {
        // console.log("Applicando", this, oldValue);

        formData.append(SC_CrewBase.inputKeys.internalID, String(this.internalID));

        if (this.alias !== oldValue?.alias && this.alias != null)
            formData.append(SC_CrewBase.inputKeys.alias, this.alias);
        if (this.states !== oldValue?.states && this.states != null)
            formData.append(SC_CrewBase.inputKeys.states, (this.states).join(","));
        if (this.active !== oldValue?.active && this.active != null)
            formData.append(SC_CrewBase.inputKeys.active, String(this.active));
        if (this.crewType !== oldValue?.crewType && !this.crewType.is(SC_CrewType.NULL))
            formData.append(SC_CrewBase.inputKeys.crewType, String(this.crewType.toAPI()));
    }
}
